import type { SiteResponse } from '@on3/api';
import { On3IconAnchorArrow } from '@on3/icons';
import { forwardSlashCheck } from '@on3/ui-lib/utils/forwardSlashCheck';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { FanSiteAvatar } from 'components/Image/FanSiteAvatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { Text } from 'components/UI/Typography/Text';
import Link from 'next/link';
import { useMemo } from 'react';

import styles from './TeamItem.module.scss';

interface TeamProps {
  site: SiteResponse;
  siblingSite?: SiteResponse | null;
}

export const TeamItem = ({ site, siblingSite }: TeamProps) => {
  const { organization, url, siblingSiteKey, primaryColor } = site;

  const siteBackground = useMemo(
    () => ({ backgroundColor: primaryColor || '#000000' }),
    [primaryColor],
  );

  const formattedTeamHref = url ? forwardSlashCheck(url) : '/';
  const isKentucky = url === '/college/kentucky-wildcats';
  const avatarSlug = isKentucky ? '-plus-icon.png' : '-icon.png';

  return (
    <li className={styles.item}>
      <div className={styles.teamblock}>
        <TeamAvatar
          className={styles.avatar}
          height={22}
          slug={organization?.name}
          src={getAssetUrl(organization?.defaultAssetRes)}
          width={22}
        />
        <div>
          <span className={styles.organization}>
            {organization?.knownAs ?? organization?.name}
          </span>
          <span className={styles.mascot}>{organization?.mascot}</span>
        </div>
      </div>
      <Link className={styles.channelLink} href={formattedTeamHref}>
        Channel
        <On3IconAnchorArrow height={22} width={8} />
      </Link>
      <div className={styles.links}>
        {siblingSiteKey && siblingSite?.inIndex && (
          <Link
            className={[styles.link, styles.site].join(' ')}
            href={siblingSite?.url || '/'}
          >
            <>
              <span className={styles.siblinglogo} style={siteBackground}>
                <FanSiteAvatar
                  height={22}
                  name={organization?.name || 'default'}
                  slug={avatarSlug}
                  width={22}
                />
              </span>
              <Text
                className={styles.sitename}
                component="span"
                variant="subtitle1"
              >
                {
                  siblingSite?.siteCategories?.find(
                    (s) => s.categoryType === 'Home',
                  )?.categoryName
                }
              </Text>
            </>
          </Link>
        )}
        {isKentucky && siblingSite?.inIndex && (
          <Link
            className={[styles.link, styles.site].join(' ')}
            href={'/teams/kentucky-wildcats/'}
          >
            <>
              <span className={styles.siblinglogo} style={siteBackground}>
                <FanSiteAvatar height={22} name={'kentucky'} width={22} />
              </span>
              <Text
                className={styles.sitename}
                component="span"
                variant="subtitle1"
              >
                KSR
              </Text>
            </>
          </Link>
        )}
      </div>
    </li>
  );
};
